import React, { useState, useEffect, memo, useCallback } from 'react';
import { format, isBefore, subMonths } from 'date-fns';

const ContractNotifTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const fetchContractData = useCallback(async () => {
  try {
    setLoading(true);
    setError(null);

    const response = await fetch('/api/contrat/getMissingContracts');

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    const result = await response.json();

    if (Array.isArray(result)) {
      const twoMonthsAgo = subMonths(new Date(), 2);
      const filteredResult = result
        .filter(item => !item.abandoned && isBefore(new Date(item.date), twoMonthsAgo))
        .map(item => ({
          ...item,
          operation: item.operation.charAt(0).toUpperCase() + item.operation.slice(1).toLowerCase(),
          sci_sccv: item.sci_sccv.toUpperCase()
        }));
      setData(filteredResult);
    } else {
      setData([]);
    }
  } catch (err) {
    console.error('Erreur lors du chargement des données Contrat:', err);
    setError(err.message);
    setData([]);
  } finally {
    setLoading(false);
  }
}, [setLoading, setError, setData]);

  useEffect(() => {
    fetchContractData();
  }, [fetchContractData]);

  const calculateTooltipPosition = useCallback((mouseX, mouseY) => {
    const tooltipWidth = 300; // Largeur approximative du tooltip (plus large car plus de contenu)
    const tooltipHeight = 250; // Hauteur approximative du tooltip (plus haut car plus de lignes)
    const offset = 10; // Décalage par rapport au curseur
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let x = mouseX + offset;
    let y = mouseY + offset;
    
    // Vérifier si le tooltip dépasse à droite
    if (x + tooltipWidth > viewportWidth) {
      x = mouseX - tooltipWidth - offset;
    }
    
    // Vérifier si le tooltip dépasse en bas
    if (y + tooltipHeight > viewportHeight) {
      y = mouseY - tooltipHeight - offset;
    }
    
    // Vérifier si le tooltip dépasse à gauche (après correction droite)
    if (x < 0) {
      x = offset;
    }
    
    // Vérifier si le tooltip dépasse en haut (après correction bas)
    if (y < 0) {
      y = offset;
    }
    
    return { x, y };
  }, []);

  const handleMouseMove = useCallback((e) => {
    const position = calculateTooltipPosition(e.clientX, e.clientY);
    setTooltipPosition(position);
  }, [calculateTooltipPosition]);

  const renderContractItem = useCallback((item, index) => (
    <div
      key={item.id || index}
      className={`contract-item ${hoveredRow === index ? 'hovered' : ''}`}
      onMouseEnter={() => setHoveredRow(index)}
      onMouseLeave={() => setHoveredRow(null)}
      onMouseMove={handleMouseMove}
    >
      <span className="contract-dossier" style={!item.ndossier ? { color: '#dc3545', fontStyle: 'italic' } : {}}>
        {item.ndossier || 'Indisponible'}
      </span>
      <span className="contract-separator"> - </span>
      <span className="contract-lot" style={!item.operation ? { color: '#dc3545', fontStyle: 'italic' } : {}}>
        {item.operation.length > 40 ? item.operation.slice(0, 40) + '...' : item.operation || 'Indisponible'}
      </span>
      <span className="contract-separator"> - </span>
      <span className="contract-company" style={!item.date ? { color: '#dc3545', fontStyle: 'italic' } : {}}>
        {item.date ? format(new Date(item.date), 'dd/MM/yyyy') : '...'}
      </span>
    </div>
  ), [hoveredRow, handleMouseMove]);

  if (loading) {
    return (
      <div className="contract-notif-table loading">
        <div className="loading-spinner">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="contract-notif-table error">
        <div className="error-message">
          Erreur: {error}
        </div>
        <button
          onClick={fetchContractData}
          className="retry-button"
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="contract-notif-table empty">
        <div className="empty-message">Aucun contrat sans réponse trouvé.</div>
      </div>
    );
  }

  return (
    <div className="contract-notif-table">
      <div className="contract-notif-header">
        <span>Contrat{data.length > 1 && 's'} sans réponse ({data.length})</span>
      </div>
      <div className="contract-notif-list">
        {data.map(renderContractItem)}
      </div>
      {hoveredRow !== null && (
        <div
          className="contract-notif-tooltip"
          style={{ 
            left: tooltipPosition.x, 
            top: tooltipPosition.y,
            position: 'fixed'
          }}
        >
          <p><b>Dossier:</b> <span style={!data[hoveredRow].ndossier ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].ndossier || 'Indisponible'}</span></p>
          <p><b>SCI SCCV:</b> <span style={!data[hoveredRow].sci_sccv ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].sci_sccv || 'Indisponible'}</span></p>
          <p><b>Maître d'ouvrage:</b> <span style={!data[hoveredRow].prenom ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].prenom ? `${data[hoveredRow].prenom} ${data[hoveredRow].nom || ''}` : 'Indisponible'}</span></p>
          <p><b>Teléphone:</b> <span style={!data[hoveredRow].telp ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].telp || 'Indisponible'}</span></p>
          <p><b>Mail:</b> <span style={!data[hoveredRow].mail ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].mail || 'Indisponible'}</span></p>
          <p><b>Opération:</b> <span style={!data[hoveredRow].operation ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].operation || 'Indisponible'}</span></p>
          <p><b>Adresse:</b> <span style={!data[hoveredRow].adresse_op ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{`${data[hoveredRow].adresse_op}, ${data[hoveredRow].cp_op} - ${data[hoveredRow].ville_op}` || 'Indisponible'}</span></p>
          <p><b>Contrat:</b> <span style={!data[hoveredRow].date ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{format(new Date(data[hoveredRow].date), 'dd/MM/yyyy') || 'Indisponible'}</span></p>
          <p><b>Démarrage:</b> <span style={!data[hoveredRow].datedem ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{format(new Date(data[hoveredRow].datedem), 'dd/MM/yyyy') || 'Indisponible'}</span></p>
        </div>
      )}
    </div>
  );
};

export default memo(ContractNotifTable);